import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface CartItem {
  productId: string
  name: string
  sku: string
  price: number
  quantity: number
  imageUrl?: string
}

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref<CartItem[]>([])

  // Getters
  const itemCount = computed(() =>
    items.value.reduce((sum, item) => sum + item.quantity, 0)
  )

  const subtotal = computed(() =>
    items.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
  )

  const isEmpty = computed(() => items.value.length === 0)

  // Actions
  let saveTimeout: ReturnType<typeof setTimeout> | null = null
  const API_URL = (import.meta.env.VITE_API_URL as string) || 'http://localhost:4000/api/v1'

  async function saveToServer() {
    const { useAuthStore } = await import('./auth')
    const auth = useAuthStore()
    if (!auth.token) return

    try {
      await fetch(`${API_URL}/storefront/cart`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({ cart: items.value })
      })
    } catch (e) {
      console.error('Failed to save cart to server', e)
    }
  }

  function triggerSave() {
    if (saveTimeout) clearTimeout(saveTimeout)
    saveTimeout = setTimeout(() => {
      saveToServer()
    }, 2000)
  }

  async function syncWithServer() {
    const { useAuthStore } = await import('./auth')
    const auth = useAuthStore()
    if (!auth.token) return

    try {
      const res = await fetch(`${API_URL}/storefront/cart`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      })
      const result = await res.json()
      if (result.success && Array.isArray(result.data)) {
        const serverItems = result.data as CartItem[]

        if (items.value.length === 0) {
          items.value = serverItems
        } else {
          // Merge server items into local
          for (const sItem of serverItems) {
            const existing = items.value.find(i => i.productId === sItem.productId)
            if (existing) {
              // If exists in both, keep the larger quantity or local?
              // We'll keep local if it was recently modified, but we don't track timestamp.
              // We'll trust local is more recent if user is active.
              // But if I logged in, server might have old items.
              // Let's just Add server items that are NOT in local.
              // And for duplicates, we'll assume the User wants the local version (current session).
            } else {
              items.value.push(sItem)
            }
          }
        }
        saveToStorage()
        // Update server with the merged result
        await saveToServer()
      }
    } catch (e) { console.error('Sync cart failed', e) }
  }

  function addItem(product: Omit<CartItem, 'quantity'>, quantity = 1) {
    const existing = items.value.find(item => item.productId === product.productId)

    if (existing) {
      existing.quantity += quantity
    } else {
      items.value.push({ ...product, price: Number(product.price), quantity })
    }

    saveToStorage()
    triggerSave()
  }

  function updateQuantity(productId: string, quantity: number) {
    const item = items.value.find(i => i.productId === productId)
    if (item) {
      if (quantity <= 0) {
        removeItem(productId)
      } else {
        item.quantity = quantity
        saveToStorage()
        triggerSave()
      }
    }
  }

  function removeItem(productId: string) {
    const index = items.value.findIndex(i => i.productId === productId)
    if (index > -1) {
      items.value.splice(index, 1)
      saveToStorage()
      triggerSave()
    }
  }

  function clearCart() {
    items.value = []
    saveToStorage()
    triggerSave()
  }

  function saveToStorage() {
    localStorage.setItem('click-electra-cart', JSON.stringify(items.value))
  }

  function loadFromStorage() {
    const stored = localStorage.getItem('click-electra-cart')
    if (stored) {
      try {
        items.value = JSON.parse(stored)
      } catch {
        items.value = []
      }
    }
  }

  // Initialize
  loadFromStorage()

  return {
    items,
    itemCount,
    subtotal,
    isEmpty,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    syncWithServer
  }
})
